<template>
  <div class="main-panel">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" @click="goMainPanel" />
    </div>

    <header class="header">
      <div class="header__left">
        <div class="avatar" aria-hidden="true">⚙</div>
        <div>
        <h1>Ayuda · Administracion de reportes</h1>
        <p class="sub">Actualiza estatus y agrega comentario administrativo</p>
        </div>
      </div>
      <div class="header__right">
        <button class="btn btn--ghost" @click="goBack">Regresar</button>
        <button class="btn btn--ghost" @click="loadReports">Actualizar</button>
      </div>
    </header>

    <section class="card card--admin">
      <div v-if="error" class="error-banner" role="alert">
        <span>{{ error }}</span>
        <button class="btn btn--ghost" @click="loadReports">Reintentar</button>
      </div>

      <div v-if="loadingReports" class="skeleton-grid">
        <div v-for="n in 5" :key="n" class="skeleton-card"></div>
      </div>

      <div v-else-if="reports.length" class="report-list">
        <article v-for="report in paginatedReports" :key="report.id" class="report-item">
          <div class="report-head">
            <strong class="report-id">#{{ report.id }} · {{ report.username_snapshot }}</strong>
            <span>{{ formatDate(report.created_at) }}</span>
          </div>
          <p class="description">
            {{ shortText(report.description, report.id, 'description', 180) }}
            <button
              v-if="needsTruncate(report.description, 180)"
              class="link-btn"
              @click="toggleExpand(report.id, 'description')"
            >
              {{ isExpanded(report.id, 'description') ? 'menos' : 'mas' }}
            </button>
          </p>
          <p class="meta">
            <b>URL:</b>
            {{ shortText(report.target_url, report.id, 'url', 95) }}
            <button
              v-if="needsTruncate(report.target_url, 95)"
              class="link-btn"
              @click="toggleExpand(report.id, 'url')"
            >
              {{ isExpanded(report.id, 'url') ? 'menos' : 'mas' }}
            </button>
          </p>
          <div class="chip-row">
            <span :class="['priority-chip', priorityClass(report.priority)]">{{ report.priority_label || priorityLabel(report.priority) }}</span>
            <span :class="['status-chip', statusClass(report.status)]">{{ report.status_label }}</span>
          </div>

          <div class="editor-grid">
            <label class="field">
              <span>Nuevo estatus</span>
              <select v-model="report.statusDraft" :class="statusClass(report.statusDraft)">
                <option value="en_revision">En revisión</option>
                <option value="en_lista_espera">En lista de espera</option>
                <option value="rechazado">Rechazado</option>
                <option value="completado">Completado</option>
              </select>
            </label>
            <label class="field">
              <span>Comentario admin</span>
              <textarea v-model="report.adminCommentDraft" rows="3"></textarea>
            </label>
          </div>
          <div class="actions">
            <button class="btn btn--primary" :disabled="Boolean(report.saving)" @click="saveReport(report)">Guardar cambios</button>
          </div>
        </article>
      </div>
      <div class="pagination" v-if="reports.length > pageSize">
        <button @click="prevPage" :disabled="currentPage === 1" class="pg-btn" aria-label="Página anterior">
          <img src="@/assets/angulo-izquierdo.png" alt="Previous Page" />
        </button>
        <span
          v-for="pageNumber in totalPages"
          :key="pageNumber"
          @click="goToPage(pageNumber)"
          :class="['pg-num', { active: pageNumber === currentPage }]"
        >
          {{ pageNumber }}
        </span>
        <button @click="nextPage" :disabled="currentPage >= totalPages" class="pg-btn" aria-label="Página siguiente">
          <img src="@/assets/angulo-derecho.png" alt="Next Page" />
        </button>
      </div>
      <div v-else class="empty-state">
        <div class="empty-icon">🧩</div>
        <p class="empty">No hay reportes registrados.</p>
        <button class="btn btn--primary" @click="loadReports">Actualizar</button>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'BugReportAdminPanel',
  data() {
    return {
      permissions: [],
      reports: [],
      currentPage: 1,
      pageSize: 5,
      loadingReports: false,
      error: '',
      expandedFields: {},
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.reports.length / this.pageSize))
    },
    paginatedReports() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.reports.slice(start, start + this.pageSize)
    },
  },
  created() {
    this.fetchCurrentUser()
  },
  methods: {
    hasPerm(perm) {
      if (!perm) return false
      if (this.permissions.includes(perm)) return true
      return this.permissions.includes(`access_control.${perm}`)
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.permissions = data.permissions || []
        if (!this.hasPerm('maintenance.is_bug_report_admin')) {
          this.$router.push({ name: 'MainPanel' })
          return
        }
        this.loadReports()
      } catch (error) {
        this.$router.push({ name: 'MainPanel' })
      }
    },
    async loadReports() {
      const apiDirectory = process.env.API_DIRECTORY
      this.error = ''
      this.loadingReports = true
      try {
        const { data } = await axios.get(`${apiDirectory}/bug-reports/admin`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.reports = (Array.isArray(data) ? data : []).map((item) => ({
          ...item,
          statusDraft: item.status,
          adminCommentDraft: item.admin_comment || '',
          saving: false,
        }))
        this.currentPage = 1
      } catch (error) {
        this.error = 'No se pudieron cargar los reportes.'
      } finally {
        this.loadingReports = false
      }
    },
    async saveReport(report) {
      const apiDirectory = process.env.API_DIRECTORY
      this.error = ''
      report.saving = true
      try {
        const { data } = await axios.put(
          `${apiDirectory}/bug-reports/admin/${report.id}/`,
          {
            status: report.statusDraft,
            admin_comment: report.adminCommentDraft,
          },
          {
            headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
          }
        )
        report.status = data.status
        report.status_label = data.status_label
        report.admin_comment = data.admin_comment
      } catch (error) {
        this.error = 'No se pudo guardar el reporte.'
      } finally {
        report.saving = false
      }
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('es-MX')
    },
    priorityLabel(value) {
      const map = {
        sugerencia: 'Sugerencia',
        baja: 'Baja',
        media: 'Media',
        urgente: 'Urgente',
      }
      return map[value] || 'Sugerencia'
    },
    priorityClass(value) {
      const map = {
        sugerencia: 'priority-sugerencia',
        baja: 'priority-baja',
        media: 'priority-media',
        urgente: 'priority-urgente',
      }
      return map[value] || 'priority-sugerencia'
    },
    statusClass(value) {
      const map = {
        en_revision: 'status-review',
        en_lista_espera: 'status-wait',
        rechazado: 'status-rejected',
        completado: 'status-done',
      }
      return map[value] || 'status-review'
    },
    goToPage(pageNumber) {
      this.currentPage = pageNumber
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage += 1
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage -= 1
    },
    fieldKey(reportId, fieldName) {
      return `${reportId}:${fieldName}`
    },
    toggleExpand(reportId, fieldName) {
      const key = this.fieldKey(reportId, fieldName)
      this.expandedFields = {
        ...this.expandedFields,
        [key]: !this.expandedFields[key],
      }
    },
    isExpanded(reportId, fieldName) {
      return Boolean(this.expandedFields[this.fieldKey(reportId, fieldName)])
    },
    needsTruncate(value, maxChars) {
      return Boolean(value) && String(value).length > maxChars
    },
    shortText(value, reportId, fieldName, maxChars) {
      const text = value || ''
      if (!this.needsTruncate(text, maxChars) || this.isExpanded(reportId, fieldName)) {
        return text
      }
      return `${String(text).slice(0, maxChars)}...`
    },
    goMainPanel() {
      this.$router.push({ name: 'MainPanel' })
    },
    goBack() {
      this.$router.push({ path: '/ayuda' })
    },
  },
}
</script>

<style scoped>
.main-panel {
  --radius-lg: 16px;
  --radius-md: 10px;
  --line: #e5e7eb;
  --shadow-soft: 0 10px 20px rgba(15, 23, 42, 0.08);
  min-height: 100vh;
  background: #f3f6fd;
  padding: 12vh 24px 48px;
}
.nav-bar-operations { position: absolute; top: 0; left: 0; width: 100vw; height: 10vh; background: #ffc434; display: flex; align-items: center; padding-left: 16px; }
.logo-button { height: 55%; object-fit: contain; cursor: pointer; }
.header {
  margin-top: 10px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: var(--radius-lg);
  padding: 12px 16px;
  backdrop-filter: blur(10px);
}
.header__left { display: flex; align-items: center; gap: 12px; }
.header__right { display: flex; align-items: center; gap: 8px; }
.avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #ffc434, #ffe188);
  color: #4d3600;
  font-weight: 800;
}
.header h1 { margin: 0; font-size: 1.2rem; color: #0f172a; }
.sub { margin: 3px 0 0; color: #667085; }
.card { background: #fff; border: 1px solid var(--line); border-radius: var(--radius-lg); padding: 16px; box-shadow: var(--shadow-soft); }
.card--admin {
  max-width: 1240px;
  margin: 0 auto;
}
.report-list { display: grid; gap: 12px; }
.report-item {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.16s ease, transform 0.16s ease;
}
.report-item:hover {
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.1);
  transform: translateY(-1px);
}
.report-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.report-id { font-size: 1.02rem; color: #0f172a; }
.description {
  margin: 10px 0;
  white-space: pre-wrap;
  font-size: 1rem;
  line-height: 1.5;
  color: #111827;
}
.meta {
  margin: 0 0 10px;
  color: #334155;
  font-size: 0.96rem;
}
.chip-row { display: flex; gap: 8px; margin: 0 0 10px; flex-wrap: wrap; }
.editor-grid { display: grid; grid-template-columns: 260px 1fr; gap: 10px; }
.field { display: grid; gap: 6px; }
.field span { font-size: 0.95rem; font-weight: 600; color: #344054; }
.field select, .field textarea {
  border: 1px solid #d0d7de;
  border-radius: var(--radius-md);
  padding: 8px 10px;
  font: inherit;
  transition: all 0.14s ease;
}
.field select:focus, .field textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
}
.actions { display: flex; justify-content: flex-end; margin-top: 8px; }
.btn { border-radius: var(--radius-md); padding: 9px 12px; border: 1px solid transparent; cursor: pointer; transition: all 0.16s ease; }
.btn--ghost { background: #fff; border-color: #d0d7de; }
.btn--primary { background: #2563eb; color: #fff; }
.btn--primary:hover { box-shadow: 0 10px 18px rgba(37, 99, 235, 0.34); }
.btn:focus { outline: none; box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.2); }
.empty { margin: 0; color: #667085; }
.error-banner {
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(240, 68, 56, 0.35);
  background: rgba(254, 228, 226, 0.8);
  color: #912018;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.empty-state {
  min-height: 260px;
  border: 1px dashed #d0d7de;
  border-radius: 12px;
  display: grid;
  place-items: center;
  text-align: center;
  gap: 8px;
  padding: 20px;
}
.empty-icon { font-size: 2rem; }
.link-btn {
  border: 0;
  background: transparent;
  color: #1d4ed8;
  cursor: pointer;
  font-size: .8rem;
  font-weight: 700;
  margin-left: 6px;
}
.priority-chip, .status-chip {
  display: inline-flex;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: .82rem;
  font-weight: 700;
  border: 1px solid transparent;
}
.priority-sugerencia { background: rgba(37, 99, 235, 0.1); color: #1e40af; border-color: rgba(37, 99, 235, 0.3); }
.priority-baja { background: rgba(8, 145, 178, 0.12); color: #155e75; border-color: rgba(8, 145, 178, 0.35); }
.priority-media { background: rgba(217, 119, 6, 0.12); color: #92400e; border-color: rgba(217, 119, 6, 0.35); }
.priority-urgente { background: rgba(220, 38, 38, 0.14); color: #b91c1c; border-color: rgba(220, 38, 38, 0.38); }
.status-review { background: rgba(37, 99, 235, 0.1); color: #1e40af; border-color: rgba(37, 99, 235, 0.3); }
.status-wait { background: rgba(217, 119, 6, 0.12); color: #92400e; border-color: rgba(217, 119, 6, 0.35); }
.status-rejected { background: rgba(220, 38, 38, 0.12); color: #b91c1c; border-color: rgba(220, 38, 38, 0.35); }
.status-done { background: rgba(22, 163, 74, 0.13); color: #166534; border-color: rgba(22, 163, 74, 0.35); }
.skeleton-grid { display: grid; gap: 10px; }
.skeleton-card {
  height: 112px;
  border-radius: 12px;
  background: linear-gradient(100deg, #eef2ff 20%, #f8fbff 50%, #eef2ff 80%);
  background-size: 220% 100%;
  animation: shimmer 1.2s infinite linear;
}
@keyframes shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
.pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 6px;
  padding: 12px 8px 4px;
}
.pg-btn {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 2px 6px;
  cursor: pointer;
}
.pg-btn:disabled { opacity: .5; cursor: not-allowed; }
.pg-btn img { width: 18px; height: auto; display: block; }
.pg-num {
  min-width: 30px;
  text-align: center;
  padding: 5px 8px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: #fff;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
}
.pg-num.active {
  background: #ffc434;
  border-color: #e0b12b;
}
@media (max-width: 900px) {
  .editor-grid { grid-template-columns: 1fr; }
  .header { flex-direction: column; align-items: flex-start; }
  .header__right { width: 100%; justify-content: space-between; }
}
@media (min-width: 1400px) {
  .card--admin {
    max-width: 1120px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .report-item,
  .btn,
  .field select,
  .field textarea,
  .skeleton-card {
    transition: none;
    animation: none;
  }
}
</style>
